import {
  FLOW_NODE_LONG_SIDE,
  clampFlowNodeRatio,
  flowNodeAspectRatio,
  flowNodeWidth,
  type FlowNodeAspectSource,
} from "@/lib/flow-node-size"

// Константы и порядок раскладки должны совпадать с app/services/it2tv/flow/arrange_nodes.rb.
// Паритет держат общие golden-фикстуры test/fixtures/files/flow_arrange/*.json
// (flow-arrange.parity.test.ts и arrange_nodes_parity_test.rb).
/** Зазор между колонками одного куска. */
const GAP_X = 120
/** Зазор между карточками. Перекрывает заголовок, который висит над карточкой. */
const GAP_Y = 64
/** Зазор между отдельными связными кусками и сеткой несвязанных нод. */
const COMPONENT_GAP = 160
/** Источник чуть меньше обычных 360, выход чуть больше. Середина остаётся обычной. */
const SOURCE_LONG_SIDE = 320
const SINK_LONG_SIDE = 400

export type FlowArrangeInput = {
  sourceId: number
  position: number
}

export type FlowArrangeNode = {
  id: number
  x: number
  y: number
  width: number
  height: number
  inputs: FlowArrangeInput[]
  /** Ширина карточки для сохранения. `null` — обычный размер. У тестовых коробок поля нет. */
  displayWidth?: number | null
}

export type FlowArrangeRow = FlowNodeAspectSource & {
  id: number
  position_x: number
  position_y: number
  inputs?: { source_node_id: number; position: number }[]
}

export type FlowArrangePoint = {
  id: number
  x: number
  y: number
}

type Point = { x: number; y: number }

/**
 * Слева направо по связям: источники в колонке 0, потребители правее.
 * Левый верхний угол набора остаётся на месте. Меньше двух нод — пустой список.
 */
export function arrangeFlowNodes(nodes: readonly FlowArrangeNode[]): FlowArrangePoint[] {
  if (nodes.length < 2) return []

  const prepared = nodes.map(sanitizeNode)
  const byId = new Map(prepared.map((node) => [node.id, node]))
  const { incoming, outgoing, undirected } = graphWithin(prepared)
  const { components, isolates } = splitGroups(prepared, undirected)

  const placed = new Map<number, Point>()
  let cursorX = 0
  const orderedComponents = [...components].sort((left, right) => compareOrigin(left, right, byId))
  for (const ids of orderedComponents) {
    const local = placeComponent(ids, byId, incoming, outgoing)
    const width = spanWidth(local, byId)
    for (const [id, point] of local) placed.set(id, { x: point.x + cursorX, y: point.y })
    cursorX += width + COMPONENT_GAP
  }

  const isolateNodes = isolates.flatMap((id) => {
    const node = byId.get(id)
    return node ? [node] : []
  })
  if (isolateNodes.length > 0) {
    const originX = orderedComponents.length > 0 ? cursorX : 0
    for (const [id, point] of placeGrid(isolateNodes, originX)) placed.set(id, point)
  }

  const anchored = anchorToOrigin(placed, prepared)
  return prepared.map((node) => {
    const point = anchored.get(node.id) ?? { x: node.x, y: node.y }
    return { id: node.id, x: point.x, y: point.y }
  })
}

/** Размер карточки под роль в наборе, затем коробка для раскладки уже в этом размере. */
export function flowArrangeNodesFromRows(rows: readonly FlowArrangeRow[]): FlowArrangeNode[] {
  const displayWidths = flowArrangeDisplayWidths(rows)
  return rows.map((row) => {
    const ratio = clampFlowNodeRatio(flowNodeAspectRatio(row))
    const displayWidth = displayWidths.get(row.id) ?? null
    const width = flowNodeWidth(ratio, displayWidth)
    return {
      id: row.id,
      x: row.position_x,
      y: row.position_y,
      width,
      height: Math.max(1, Math.round(width / ratio)),
      displayWidth,
      inputs: (row.inputs ?? []).map((input) => ({
        sourceId: input.source_node_id,
        position: input.position,
      })),
    }
  })
}

function flowArrangeDisplayWidths(rows: readonly FlowArrangeRow[]): Map<number, number | null> {
  const roles = arrangeRoles(rows)
  const widths = new Map<number, number | null>()
  for (const row of rows) {
    const role = roles.get(row.id) ?? "plain"
    if (role === "plain") {
      widths.set(row.id, null)
      continue
    }
    const ratio = clampFlowNodeRatio(flowNodeAspectRatio(row))
    const base = flowNodeWidth(ratio)
    const longSide = role === "source" ? SOURCE_LONG_SIDE : SINK_LONG_SIDE
    widths.set(row.id, Math.round((base * longSide) / FLOW_NODE_LONG_SIDE))
  }
  return widths
}

/** Нет входа — источник, нет выхода — конец. Середина и ноды без связей остаются обычными. */
function arrangeRoles(rows: readonly FlowArrangeRow[]): Map<number, "source" | "sink" | "plain"> {
  const roles = new Map<number, "source" | "sink" | "plain">()
  const stubs: FlowArrangeNode[] = rows.map((row) => ({
    id: row.id,
    x: row.position_x,
    y: row.position_y,
    width: 1,
    height: 1,
    inputs: (row.inputs ?? []).map((input) => ({
      sourceId: input.source_node_id,
      position: input.position,
    })),
  }))
  const { incoming, outgoing, undirected } = graphWithin(stubs)
  const isolateIds = new Set(splitGroups(stubs, undirected).isolates)
  for (const row of rows) {
    if (isolateIds.has(row.id)) {
      roles.set(row.id, "plain")
      continue
    }
    const hasIn = (incoming.get(row.id) ?? []).length > 0
    const hasOut = (outgoing.get(row.id) ?? []).length > 0
    if (!hasIn) roles.set(row.id, "source")
    else if (!hasOut) roles.set(row.id, "sink")
    else roles.set(row.id, "plain")
  }
  return roles
}

function sanitizeNode(node: FlowArrangeNode): FlowArrangeNode {
  return {
    ...node,
    width: positive(node.width),
    height: positive(node.height),
    inputs: node.inputs ?? [],
  }
}

function positive(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 1
}

function graphWithin(nodes: readonly FlowArrangeNode[]): {
  incoming: Map<number, FlowArrangeInput[]>
  outgoing: Map<number, number[]>
  undirected: Map<number, number[]>
} {
  const ids = new Set(nodes.map((node) => node.id))
  const incoming = new Map<number, FlowArrangeInput[]>()
  const outgoing = new Map<number, number[]>()
  const undirected = new Map<number, number[]>()
  for (const node of nodes) {
    incoming.set(node.id, [])
    outgoing.set(node.id, [])
    undirected.set(node.id, [])
  }

  for (const node of nodes) {
    const seen = new Set<number>()
    for (const input of node.inputs) {
      if (!ids.has(input.sourceId) || input.sourceId === node.id || seen.has(input.sourceId)) continue
      seen.add(input.sourceId)
      incoming.get(node.id)?.push(input)
      outgoing.get(input.sourceId)?.push(node.id)
      link(undirected, node.id, input.sourceId)
      link(undirected, input.sourceId, node.id)
    }
  }

  return { incoming, outgoing, undirected }
}

function link(undirected: Map<number, number[]>, from: number, to: number) {
  const list = undirected.get(from)
  if (!list || list.includes(to)) return
  list.push(to)
}

function splitGroups(
  nodes: readonly FlowArrangeNode[],
  undirected: Map<number, number[]>,
): { components: number[][]; isolates: number[] } {
  const seen = new Set<number>()
  const isolates: number[] = []
  for (const node of nodes) {
    if ((undirected.get(node.id) ?? []).length > 0) continue
    isolates.push(node.id)
    seen.add(node.id)
  }

  const components: number[][] = []
  for (const node of nodes) {
    if (seen.has(node.id)) continue
    const stack = [node.id]
    const component: number[] = []
    seen.add(node.id)
    while (stack.length > 0) {
      const id = stack.pop()
      if (id == null) continue
      component.push(id)
      for (const next of undirected.get(id) ?? []) {
        if (seen.has(next)) continue
        seen.add(next)
        stack.push(next)
      }
    }
    components.push(component)
  }

  return { components, isolates }
}

function compareOrigin(left: readonly number[], right: readonly number[], byId: Map<number, FlowArrangeNode>): number {
  const a = originOf(left, byId)
  const b = originOf(right, byId)
  return a.x - b.x || a.y - b.y || a.id - b.id
}

function originOf(ids: readonly number[], byId: Map<number, FlowArrangeNode>): { x: number; y: number; id: number } {
  let x = Number.POSITIVE_INFINITY
  let y = Number.POSITIVE_INFINITY
  let id = Number.POSITIVE_INFINITY
  for (const nodeId of ids) {
    const node = byId.get(nodeId)
    if (!node) continue
    if (node.x < x || (node.x === x && node.y < y)) {
      x = node.x
      y = node.y
    }
    id = Math.min(id, nodeId)
  }
  return { x, y, id }
}

/** Ранг — самый длинный путь от источников. Ребро в серый узел (цикл) пропускается. */
function assignRanks(ids: readonly number[], incoming: Map<number, FlowArrangeInput[]>): Map<number, number> {
  const rank = new Map<number, number>()
  const visiting = new Set<number>()

  const visit = (id: number): number => {
    if (visiting.has(id)) return 0
    const known = rank.get(id)
    if (known != null) return known
    visiting.add(id)
    let best = 0
    for (const input of incoming.get(id) ?? []) {
      if (visiting.has(input.sourceId)) continue
      best = Math.max(best, visit(input.sourceId) + 1)
    }
    visiting.delete(id)
    rank.set(id, best)
    return best
  }

  for (const id of ids) visit(id)
  return rank
}

function orderColumns(
  ids: readonly number[],
  byId: Map<number, FlowArrangeNode>,
  incoming: Map<number, FlowArrangeInput[]>,
  outgoing: Map<number, number[]>,
): { columns: Map<number, FlowArrangeNode[]>; rankOf: Map<number, number> } {
  const rankOf = assignRanks(ids, incoming)
  const columns = new Map<number, FlowArrangeNode[]>()
  for (const id of ids) {
    const node = byId.get(id)
    if (!node) continue
    const rank = rankOf.get(id) ?? 0
    const column = columns.get(rank) ?? []
    column.push(node)
    columns.set(rank, column)
  }

  const ranks = [...columns.keys()].sort((a, b) => a - b)
  for (const rank of ranks) {
    columns.get(rank)?.sort((a, b) => a.y - b.y || a.x - b.x || a.id - b.id)
  }

  for (const rank of ranks) {
    const column = columns.get(rank) ?? []
    const scored = column.map((node, index) => ({
      node,
      desired: barycenter(node, rank, columns, rankOf, incoming, outgoing, index),
    }))
    scored.sort((a, b) => a.desired - b.desired || a.node.y - b.node.y || a.node.x - b.node.x || a.node.id - b.node.id)
    columns.set(
      rank,
      scored.map((entry) => entry.node),
    )
  }

  applyReferenceOrder(columns, ids, byId, incoming, rankOf)
  return { columns, rankOf }
}

function barycenter(
  node: FlowArrangeNode,
  rank: number,
  columns: Map<number, FlowArrangeNode[]>,
  rankOf: Map<number, number>,
  incoming: Map<number, FlowArrangeInput[]>,
  outgoing: Map<number, number[]>,
  fallback: number,
): number {
  const neighbors: number[] = []
  for (const input of incoming.get(node.id) ?? []) neighbors.push(input.sourceId)
  for (const targetId of outgoing.get(node.id) ?? []) neighbors.push(targetId)

  const indexes: number[] = []
  for (const neighborId of neighbors) {
    const neighborRank = rankOf.get(neighborId)
    if (neighborRank == null || neighborRank === rank) continue
    const index = (columns.get(neighborRank) ?? []).findIndex((item) => item.id === neighborId)
    if (index >= 0) indexes.push(index)
  }
  return indexes.length > 0 ? median(indexes) : fallback
}

/** Источники одного потребителя в одной колонке идут сверху вниз по position входа. */
function applyReferenceOrder(
  columns: Map<number, FlowArrangeNode[]>,
  ids: readonly number[],
  byId: Map<number, FlowArrangeNode>,
  incoming: Map<number, FlowArrangeInput[]>,
  rankOf: Map<number, number>,
) {
  const targets = [...ids].sort((a, b) => (rankOf.get(a) ?? 0) - (rankOf.get(b) ?? 0) || a - b)
  for (const targetId of targets) {
    const groups = new Map<number, FlowArrangeInput[]>()
    for (const input of incoming.get(targetId) ?? []) {
      const rank = rankOf.get(input.sourceId)
      if (rank == null) continue
      const group = groups.get(rank) ?? []
      group.push(input)
      groups.set(rank, group)
    }
    for (const [rank, group] of groups) {
      if (group.length < 2) continue
      const column = columns.get(rank)
      if (!column) continue
      const slots = group
        .map((input) => column.findIndex((node) => node.id === input.sourceId))
        .filter((index) => index >= 0)
        .sort((a, b) => a - b)
      const ordered = [...group].sort((a, b) => a.position - b.position || a.sourceId - b.sourceId)
      if (slots.length !== ordered.length) continue
      slots.forEach((slot, index) => {
        const source = byId.get(ordered[index]?.sourceId ?? -1)
        if (source) column[slot] = source
      })
    }
  }
}

function placeComponent(
  ids: readonly number[],
  byId: Map<number, FlowArrangeNode>,
  incoming: Map<number, FlowArrangeInput[]>,
  outgoing: Map<number, number[]>,
): Map<number, Point> {
  const { columns } = orderColumns(ids, byId, incoming, outgoing)
  const heights = new Map<number, number>()
  for (const id of ids) {
    const node = byId.get(id)
    if (node) heights.set(id, node.height)
  }
  const placed = new Map<number, Point>()
  let cursorX = 0
  for (const rank of [...columns.keys()].sort((a, b) => a - b)) {
    const column = columns.get(rank) ?? []
    let cursorY = Number.NEGATIVE_INFINITY
    let columnWidth = 0
    for (const node of column) {
      const centers = neighborCenters(node, placed, incoming, outgoing, heights)
      const idealTop = centers.length > 0 ? median(centers) - node.height / 2 : 0
      const y = cursorY === Number.NEGATIVE_INFINITY ? idealTop : Math.max(idealTop, cursorY)
      placed.set(node.id, { x: cursorX, y })
      cursorY = y + node.height + GAP_Y
      columnWidth = Math.max(columnWidth, node.width)
    }
    cursorX += columnWidth + GAP_X
  }
  return shiftToOrigin(placed)
}

function neighborCenters(
  node: FlowArrangeNode,
  placed: Map<number, Point>,
  incoming: Map<number, FlowArrangeInput[]>,
  outgoing: Map<number, number[]>,
  heights: Map<number, number>,
): number[] {
  const centers: number[] = []
  const push = (id: number) => {
    const point = placed.get(id)
    const height = heights.get(id)
    if (!point || height == null) return
    centers.push(point.y + height / 2)
  }
  for (const input of incoming.get(node.id) ?? []) push(input.sourceId)
  for (const targetId of outgoing.get(node.id) ?? []) push(targetId)
  return centers
}

function placeGrid(nodes: readonly FlowArrangeNode[], originX: number): Map<number, Point> {
  const sorted = [...nodes].sort((a, b) => a.y - b.y || a.x - b.x || a.id - b.id)
  const columns = Math.max(1, Math.ceil(Math.sqrt(sorted.length)))
  const rows = Math.ceil(sorted.length / columns)
  const columnWidths = Array.from({ length: columns }, () => 0)
  const rowHeights = Array.from({ length: rows }, () => 0)
  sorted.forEach((node, index) => {
    const column = index % columns
    const row = Math.floor(index / columns)
    columnWidths[column] = Math.max(columnWidths[column] ?? 0, node.width)
    rowHeights[row] = Math.max(rowHeights[row] ?? 0, node.height)
  })

  const columnX: number[] = []
  let x = originX
  for (let column = 0; column < columns; column += 1) {
    columnX.push(x)
    x += (columnWidths[column] ?? 0) + GAP_X
  }
  const rowY: number[] = []
  let y = 0
  for (let row = 0; row < rows; row += 1) {
    rowY.push(y)
    y += (rowHeights[row] ?? 0) + GAP_Y
  }

  const placed = new Map<number, Point>()
  sorted.forEach((node, index) => {
    const column = index % columns
    const row = Math.floor(index / columns)
    placed.set(node.id, { x: columnX[column] ?? originX, y: rowY[row] ?? 0 })
  })
  return placed
}

function spanWidth(placed: Map<number, Point>, byId: Map<number, FlowArrangeNode>): number {
  let max = 0
  for (const [id, point] of placed) {
    const node = byId.get(id)
    max = Math.max(max, point.x + (node?.width ?? 0))
  }
  return max
}

function shiftToOrigin(placed: Map<number, Point>): Map<number, Point> {
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  for (const point of placed.values()) {
    minX = Math.min(minX, point.x)
    minY = Math.min(minY, point.y)
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY) || (minX === 0 && minY === 0)) return placed
  const shifted = new Map<number, Point>()
  for (const [id, point] of placed) shifted.set(id, { x: point.x - minX, y: point.y - minY })
  return shifted
}

function anchorToOrigin(placed: Map<number, Point>, nodes: readonly FlowArrangeNode[]): Map<number, Point> {
  let originX = Number.POSITIVE_INFINITY
  let originY = Number.POSITIVE_INFINITY
  for (const node of nodes) {
    originX = Math.min(originX, node.x)
    originY = Math.min(originY, node.y)
  }
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  for (const point of placed.values()) {
    minX = Math.min(minX, point.x)
    minY = Math.min(minY, point.y)
  }
  const dx = originX - minX
  const dy = originY - minY
  const shifted = new Map<number, Point>()
  for (const [id, point] of placed) {
    shifted.set(id, { x: Math.round(point.x + dx), y: Math.round(point.y + dy) })
  }

  let roundMinX = Number.POSITIVE_INFINITY
  let roundMinY = Number.POSITIVE_INFINITY
  for (const point of shifted.values()) {
    roundMinX = Math.min(roundMinX, point.x)
    roundMinY = Math.min(roundMinY, point.y)
  }
  const fixX = originX - roundMinX
  const fixY = originY - roundMinY
  if (fixX === 0 && fixY === 0) return shifted
  const pinned = new Map<number, Point>()
  for (const [id, point] of shifted) pinned.set(id, { x: point.x + fixX, y: point.y + fixY })
  return pinned
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  const upper = sorted[mid]
  if (upper == null) return 0
  if (sorted.length % 2 === 1) return upper
  const lower = sorted[mid - 1]
  return lower == null ? upper : (lower + upper) / 2
}
